import type { EntityIconVariant } from '../../components/sidebar/EntityIcon';
import type { DrillLocation, ViewMode } from '../../types/exploration';

/**
 * Drill target for clicking a sidebar group-bars row. `iconType` (from
 * `iconTypeForListingGroup`) already tells us what kind of entity the row
 * represents — account, structural node, or status/reason leaf — so building
 * the next location is a direct field write, not a guess from the label text.
 */
export function nextLocationForListingGroup(
  current: DrillLocation,
  groupName: string,
  iconType: EntityIconVariant | null,
  view: ViewMode
): DrillLocation {
  if (iconType === 'account') {
    return { ...current, account: groupName };
  }
  if (iconType === 'cluster') {
    return { ...current, structural: { kind: 'cluster', top: { kind: 'cluster', name: groupName } } };
  }
  if (iconType === 'ec2') {
    return { ...current, structural: { kind: 'cluster', top: { kind: 'ec2' } } };
  }
  if (iconType === 'unattached') {
    return { ...current, structural: { kind: 'cluster', top: { kind: 'unattached' } } };
  }
  if (iconType === 'nodeGroup' && current.structural.kind === 'cluster') {
    return { ...current, structural: { ...current.structural, nodeGroup: groupName } };
  }
  if (
    iconType === 'instance' &&
    (current.structural.kind === 'cluster' || current.structural.kind === 'region' || current.structural.kind === 'tag')
  ) {
    return { ...current, structural: { ...current.structural, instanceId: groupName } };
  }
  if (iconType === 'region') {
    return { ...current, structural: { kind: 'region', region: groupName } };
  }
  if (iconType === 'tag') {
    return { ...current, structural: { kind: 'tag', tagValue: groupName } };
  }
  // 'volume' or null — a status/reason leaf, not a structural entity.
  // Focused activation reason rows scope via reasonLabel; plain status
  // buckets use statusLabel. Structural Unattached only keeps its own
  // reasonLabel shortcut outside the activation view (activation view rows
  // there are now plain activation-status buckets, same as everywhere else).
  if (current.focusedActivationKey) {
    return { ...current, reasonLabel: groupName };
  }
  if (
    view !== 'activation' &&
    current.structural.kind === 'cluster' &&
    current.structural.top.kind === 'unattached'
  ) {
    return { ...current, reasonLabel: groupName };
  }
  return { ...current, statusLabel: groupName };
}
